/**
 * Google Analytics Tracking Utilities
 *
 * This module provides utility functions for tracking events via Google Tag Manager.
 * Events pushed to the dataLayer will be processed by GTM and sent to Google Analytics.
 */

// Type declaration for window.dataLayer
declare global {
  interface Window {
    dataLayer: any[];
    gtag?: (...args: any[]) => void;
  }
}

/**
 * Event categories for consistent tracking
 */
export const EventCategory = {
  USER_INTERACTION: 'User Interaction',
  NAVIGATION: 'Navigation',
  VIDEO: 'Video',
} as const;

/**
 * Event actions for consistent tracking
 */
export const EventAction = {
  CLICK: 'Click',
  CLOSE: 'Close',
} as const;

/**
 * Base tracking event parameters
 */
interface TrackingEventParams {
  event_category?: string;
  event_action?: string;
  event_label?: string;
  event_value?: number;
  [key: string]: any; // Allow custom parameters
}

/**
 * Core function to push events to Google Tag Manager's dataLayer
 * @param eventName - The name of the event (this triggers GTM)
 * @param parameters - Optional event parameters
 */
export const trackEvent = (eventName: string, parameters?: TrackingEventParams): void => {
  // Check if we're in a browser environment and dataLayer exists
  if (typeof window !== 'undefined' && window.dataLayer) {
    const eventData = {
      event: eventName,
      event_timestamp: new Date().toISOString(),
      ...parameters,
    };

    window.dataLayer.push(eventData);
  }
};

/**
 * Track button or link clicks
 * @param label - Descriptive label for the button/link
 * @param category - Event category (defaults to USER_INTERACTION)
 * @param value - Optional numeric value
 * @param customParams - Additional custom parameters
 */
export const trackClick = (
  label: string,
  category: string = EventCategory.USER_INTERACTION,
  value?: number,
  customParams?: Record<string, any>
): void => {
  trackEvent('custom_click', {
    event_category: category,
    event_action: EventAction.CLICK,
    event_label: label,
    event_value: value,
    ...customParams,
  });
};

/**
 * Track navigation events (menu clicks, breadcrumbs, etc.)
 * @param label - Navigation item label
 * @param action - Action type (click, open, close)
 * @param customParams - Additional parameters
 */
export const trackNavigation = (
  label: string,
  action: string = EventAction.CLICK,
  customParams?: Record<string, any>
): void => {
  trackEvent('navigation_event', {
    event_category: EventCategory.NAVIGATION,
    event_action: action,
    event_label: label,
    ...customParams,
  });
};

/**
 * Track page views (useful for SPAs)
 * @param pagePath - The page path
 * @param pageTitle - The page title
 */
export const trackPageView = (pagePath?: string, pageTitle?: string): void => {
  trackEvent('virtual_page_view', {
    page_path: pagePath || window.location.pathname,
    page_location: window.location.href,
    page_title: pageTitle || document.title,
  });
};

/**
 * Track e-commerce events
 */
export const trackEcommerce = {
  // View item list (category page, search results)
  viewItemList: (items: any[], listName?: string, listId?: string): void => {
    trackEvent('view_item_list', {
      ecommerce: {
        item_list_name: listName,
        item_list_id: listId,
        items: items,
      },
    });
  },

  // View individual item
  viewItem: (item: any): void => {
    trackEvent('view_item', {
      ecommerce: {
        items: [item],
      },
    });
  },

  // Add to cart
  addToCart: (item: any, quantity: number = 1): void => {
    trackEvent('add_to_cart', {
      ecommerce: {
        items: [
          {
            ...item,
            quantity: quantity,
          },
        ],
      },
    });
  },

  // Remove from cart
  removeFromCart: (item: any, quantity: number = 1): void => {
    trackEvent('remove_from_cart', {
      ecommerce: {
        items: [
          {
            ...item,
            quantity: quantity,
          },
        ],
      },
    });
  },

  // Begin checkout
  beginCheckout: (items: any[], value: number, currency: string = 'USD'): void => {
    trackEvent('begin_checkout', {
      ecommerce: {
        value: value,
        currency: currency,
        items: items,
      },
    });
  },

  // Purchase
  purchase: (transactionData: any): void => {
    trackEvent('purchase', {
      ecommerce: transactionData,
    });
  },
};

/**
 * Track engagement time on page
 * Call this when the user leaves the page or at regular intervals
 */
export const trackEngagementTime = (seconds: number): void => {
  trackEvent('engagement_time', {
    event_category: EventCategory.USER_INTERACTION,
    event_action: 'Time on Page',
    event_value: seconds,
    engagement_time_seconds: seconds,
  });
};

/**
 * Video tracking utilities
 */
export const trackVideo = {
  /**
   * Track when a user clicks on a video thumbnail to open the video overlay
   * @param videoId - The video ID (e.g., YouTube video ID)
   * @param videoTitle - The title of the video
   */
  thumbnailClick: (videoId: string, videoTitle: string): void => {
    trackEvent('video_thumbnail_click', {
      event_category: EventCategory.VIDEO,
      event_action: EventAction.CLICK,
      event_label: videoTitle,
      video_id: videoId,
      video_title: videoTitle,
    });
  },

  /**
   * Track when a user closes the video overlay
   * @param videoId - The video ID
   * @param videoTitle - The title of the video
   * @param viewDurationSeconds - How long the overlay was open in seconds
   */
  overlayClose: (videoId: string, videoTitle: string, viewDurationSeconds: number): void => {
    trackEvent('video_overlay_close', {
      event_category: EventCategory.VIDEO,
      event_action: EventAction.CLOSE,
      event_label: videoTitle,
      event_value: Math.round(viewDurationSeconds),
      video_id: videoId,
      video_title: videoTitle,
      video_view_duration_seconds: Math.round(viewDurationSeconds),
    });
  },
};
